import React, { useMemo, useState } from "react";
import { Dropdown, DropdownItemProps, Icon } from "semantic-ui-react";
import { Button, Group, Label } from "react-aria-components";
import { format } from "date-fns";
import { DateTime } from "../../../components/global_components/form_components/ControlledDateTime";
import { tz } from "@date-fns/tz";
import { useTimezone } from "../../../hooks/useTimezone";
import { parseDateFilterString } from "../../../utils/parseDateFilterString";
import { DateCondition } from "../../queries/getCrmRequests";
import { IBaseControlProps, nestedErrorMessageSelector } from "./Utils";
import { Label as SemanticLabel } from "semantic-ui-react";
import { Controller, FieldError } from "react-hook-form";

const DefaultAdvancedDateFilterOptions = [
  {
    key: "day-group",
    text: <strong>Day</strong>,
    header: true,
    className: "tw-opacity-100",
    disabled: true,
  },
  { key: 2, text: "Previous 7 Days", value: "previous 7 days" },
  { key: 3, text: "Previous 30 Days", value: "previous 30 days" },
  { key: 4, text: "Yesterday", value: "yesterday" },
  { key: 5, text: "Through Yesterday", value: "through yesterday" },
  { key: 6, text: "Today", value: "today" },
  { key: 7, text: "Through Today", value: "through today" },
  { key: 8, text: "Tomorrow", value: "tomorrow" },
  { key: 9, text: "After Today", value: "after today" },
  { key: 10, text: "Through Tomorrow", value: "through tomorrow" },
  { key: 11, text: "Through Next 7 Days", value: "through next 7 days" },
  { key: 12, text: "Through Next 30 Days", value: "through next 30 days" },
  {
    key: "week-group",
    text: <strong>Week</strong>,
    header: true,
    className: "tw-opacity-100",
    disabled: true,
  },
  { key: 13, text: "Last Week", value: "last week" },
  { key: 14, text: "Last Two Weeks", value: "last two weeks" },
  { key: 15, text: "Last Week-To-Date", value: "last week-to-date" },
  { key: 16, text: "This Week", value: "this week" },
  { key: 17, text: "This Week-To-Date", value: "this week-to-date" },
  { key: 18, text: "Through End of Week", value: "through this week" },
  { key: 19, text: "Next Week", value: "next week" },

  {
    key: "month-group",
    text: <strong>Month</strong>,
    header: true,
    className: "tw-opacity-100",
    disabled: true,
  },
  { key: 20, text: "Last Month", value: "last month" },
  { key: 21, text: "Last Month-To-Date", value: "last month-to-date" },
  { key: 22, text: "Month Ago-To-Date", value: "month ago-to-date" },
  { key: 23, text: "This Month", value: "this month" },
  { key: 24, text: "This Month-To-Date", value: "this month-to-date" },
  { key: 25, text: "Next Month", value: "next month" },

  {
    key: "quarter-group",
    text: <strong>Quarter</strong>,
    header: true,
    className: "tw-opacity-100",
    disabled: true,
  },
  { key: 26, text: "Last Quarter", value: "last quarter" },
  { key: 27, text: "Last Quarter-To-Date", value: "last quarter-to-date" },
  { key: 28, text: "This Quarter", value: "this quarter" },
  { key: 29, text: "This Quarter-To-Date", value: "this quarter-to-date" },

  {
    key: "year-group",
    text: <strong>Year</strong>,
    header: true,
    className: "tw-opacity-100",
    disabled: true,
  },
  { key: 30, text: "Last Year", value: "last year" },
  { key: 31, text: "Last Fiscal Year", value: "last fiscal year" },
  { key: 32, text: "This Year", value: "this year" },
  { key: 33, text: "This Year-To-date", value: "this year-to-date" },
  { key: 34, text: "This Fiscal Year", value: "this fiscal year" },
];

const getDatesFromDateOption = (
  dateOption?: string,
  startOfFiscalYear?: number,
  daysOff?: Date[],
  timezone?: string,
):
  | {
      startDate?: Date;
      endDate?: Date;
      errorMessage?: string;
    }
  | undefined => {
  if (!startOfFiscalYear) return undefined;
  if (!dateOption) return undefined;
  if (!daysOff) return undefined;
  if (!timezone) return undefined;
  try {
    const { startDate, endDate } = parseDateFilterString(dateOption, startOfFiscalYear, daysOff, timezone);
    return { startDate: startDate || undefined, endDate: endDate || undefined };
  } catch (e) {
    const errorMessage = e instanceof Error ? e.message : "Invalid date option";
    return { errorMessage };
  }
};

const DateOperatorOptions: {
  key: string;
  text: string;
  value: DateCondition;
}[] = [
  { key: "Equal", text: "Is", value: "Equal" },
  { key: "NotEqual", text: "Is Not", value: "NotEqual" },
  { key: "LessThan", text: "Is Before", value: "LessThan" },
  { key: "LessThanOrEqual", text: "Is On Or Before", value: "LessThanOrEqual" },
  { key: "GreaterThan", text: "Is After", value: "GreaterThan" },
  { key: "GreaterThanOrEqual", text: "Is On Or After", value: "GreaterThanOrEqual" },
  { key: "Empty", text: "Is Empty", value: "Empty" },
  { key: "NotEmpty", text: "Is Not Empty", value: "NotEmpty" },
];

interface IDateRangeFilterProps {
  label: string;
  clearable?: boolean;
  shouldDefaultToRelativeDates?: boolean;
  defaultStartDate?: Date;
  defaultEndDate?: Date;
  defaultDateOption?: string;
  defaultOperator?: DateCondition;
  onRelativeDateChange?: (value: {
    startDate?: Date;
    endDate?: Date;
    dateOption?: string;
    errorMessage?: string;
  }) => void;
  onCalendarDateChange?: (value: { startDate?: Date; endDate?: Date }) => void;
  onOperatorChange?: (value: {
    operator: DateCondition;
    startDate?: Date;
    endDate?: Date;
    dateOption?: string;
  }) => void;
  className?: string;
  useTimeSelection?: boolean;
  useEmptyOperator?: boolean;
}

export function DateRangeFilter({
  label,
  clearable,
  shouldDefaultToRelativeDates = true,
  defaultDateOption,
  defaultStartDate,
  defaultEndDate,
  onRelativeDateChange,
  onCalendarDateChange,
  onOperatorChange,
  defaultOperator = "Equal",
  className,
  useTimeSelection = false,
  useEmptyOperator = true,
}: IDateRangeFilterProps) {
  const timezoneInfo = useTimezone();
  const timezone = timezoneInfo.defaultTimezoneInfo.timezone;

  const [operator, setOperator] = useState<DateCondition>(defaultOperator);

  const [startDate, setStartDate] = useState<Date | undefined>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultEndDate);

  const [dateOption, setDateOption] = useState<string | undefined>(defaultDateOption);

  const { startDate: startDateFromDefaultOptions, endDate: endDateFromDefaultOptions } =
    getDatesFromDateOption(dateOption, timezoneInfo.startOfFiscalYear, timezoneInfo.daysOff, timezone) ?? {};

  const [startRelativeDate, setStartRelativeDate] = useState<Date | undefined>(startDateFromDefaultOptions);
  const [endRelativeDate, setEndRelativeDate] = useState<Date | undefined>(endDateFromDefaultOptions);

  const calendarDefaultsPresent = !!defaultStartDate || !!defaultEndDate;
  //timezone might not be loaded in yet, so memoize this boolean when the timezone loads
  const relativeDefaultsPresent = useMemo(() => {
    return !!startDateFromDefaultOptions || !!endDateFromDefaultOptions;
  }, [startDateFromDefaultOptions, endDateFromDefaultOptions]);

  const [useRelativeDates, setUseRelativeDates] = useState<boolean>(
    (shouldDefaultToRelativeDates && !calendarDefaultsPresent) || relativeDefaultsPresent,
  );

  const dateTypeButtonText = useRelativeDates ? "Use Calendar" : "Use Relative";
  const showBothDatePickers = operator === "Equal" || operator === "NotEqual";
  //when switching from relative to calendar dates, we want to prioritize which date to show based on the operator
  const prioritizeStartDate = operator === "LessThan" || operator === "GreaterThanOrEqual";

  const shouldRenderInput = operator !== "Empty" && operator !== "NotEmpty";

  const conditionLabel = DateOperatorOptions.find((option) => option.value === operator)?.text;

  const datePreviewText = useMemo(() => {
    if (!useRelativeDates) return "";
    //remove leading 0 from date
    const formatDate = (date: Date) => format(date, "MM/dd/yyyy", { in: tz(timezone) }).replace(/^(?:00:)?0?/, "");
    const displayStartDate = !!startRelativeDate ? startRelativeDate : startDateFromDefaultOptions;
    const displayEndDate = !!endRelativeDate ? endRelativeDate : endDateFromDefaultOptions;
    switch (operator) {
      case "Equal":
        if (displayStartDate) {
          const dateText = formatDate(displayStartDate);
          if (displayEndDate) {
            return `${dateText} to ${formatDate(displayEndDate)}`;
          }
          return `On Or After ${dateText}`;
        }
        if (displayEndDate) {
          return `On Or Before ${formatDate(displayEndDate)}`;
        }
        break;
      case "NotEqual":
        if (displayStartDate) {
          const dateText = formatDate(displayStartDate);
          if (displayEndDate) {
            return `Before ${dateText} or After ${formatDate(displayEndDate)}`;
          }
          return `Before ${dateText}`;
        }
        if (displayEndDate) {
          return `After ${formatDate(displayEndDate)}`;
        }
        break;
      case "LessThan":
        if (displayStartDate) {
          return `Before ${formatDate(displayStartDate)}`;
        }
        if (displayEndDate) {
          return `Before ${formatDate(displayEndDate)}`;
        }
        break;
      case "LessThanOrEqual":
        if (displayEndDate) {
          return `On or Before ${formatDate(displayEndDate)}`;
        }
        if (displayStartDate) {
          return `On or Before ${formatDate(displayStartDate)}`;
        }
        break;
      case "GreaterThan":
        if (displayEndDate) {
          return `After ${formatDate(displayEndDate)}`;
        }
        if (displayStartDate) {
          return `After ${formatDate(displayStartDate)}`;
        }
        break;
      case "GreaterThanOrEqual":
        if (displayStartDate) {
          return `On or After ${formatDate(displayStartDate)}`;
        }
        if (displayEndDate) {
          return `On or After ${formatDate(displayEndDate)}`;
        }
        break;
      default:
        return "";
    }
  }, [
    startDate,
    endDate,
    startRelativeDate,
    endRelativeDate,
    operator,
    useRelativeDates,
    startDateFromDefaultOptions,
    endDateFromDefaultOptions,
  ]);

  return (
    <div className={className}>
      <div className='tw-flex tw-w-full tw-items-center'>
        <Label htmlFor='date-range-filter-group' className='tw-font-bold'>
          {label}
        </Label>
        <Dropdown
          id='date-range-operator-dropdown'
          className='tw-ml-4 tw-font-bold tw-text-blue'
          options={
            useEmptyOperator
              ? DateOperatorOptions
              : DateOperatorOptions.filter((option) => option.value !== "Empty" && option.value !== "NotEmpty")
          }
          defaultValue={operator}
          onChange={(_, { value }) => {
            setOperator(value as DateCondition);
            if (useRelativeDates) {
              onOperatorChange?.({
                operator: value as DateCondition,
                dateOption,
              });
              return;
            }
            const useBothDates = value === "Equal" || value === "NotEqual";
            onOperatorChange?.({
              operator: value as DateCondition,
              startDate,
              endDate: useBothDates || !startDate ? endDate : undefined,
            });
          }}
        />
      </div>
      {shouldRenderInput && (
        <div className='tw-mt-2'>
          <Group className='tw-mt-2' id='date-range-filter-group'>
            {useRelativeDates && (
              <RelativeDatePicker
                className='tw-mb-0 tw-w-full tw-pb-0'
                options={DefaultAdvancedDateFilterOptions}
                value={dateOption}
                clearable={clearable}
                onChange={({ value, startDate, endDate, errorMessage }) => {
                  if (!errorMessage) {
                    setDateOption(value);
                    setStartRelativeDate(startDate);
                    setEndRelativeDate(endDate);
                    setStartDate(startDate);
                    setEndDate(endDate);
                    onRelativeDateChange?.({ startDate, endDate, dateOption: value });
                    return;
                  }
                  onRelativeDateChange?.({ errorMessage });
                }}
              />
            )}
            {!useRelativeDates && (
              <div className='date-range-filter-container tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-0 tw-pb-1 tw-shadow-none'>
                {(showBothDatePickers || !!startDate || !endDate) && (
                  <div
                    aria-label={showBothDatePickers ? `${label} ${conditionLabel} Start` : `${label} ${conditionLabel}`}
                    className='tw-flex tw-w-full'
                  >
                    <DateTime
                      value={startDate}
                      onChange={(date?: Date) => {
                        setStartDate(date);
                        onCalendarDateChange?.({ startDate: date, endDate });
                      }}
                      className='tw-w-full tw-bg-white'
                      granularity={useTimeSelection ? "minute" : "day"}
                      clearable={clearable}
                    />
                  </div>
                )}
                {(showBothDatePickers || (!!endDate && !startDate)) && (
                  <>
                    {showBothDatePickers && (
                      <Icon className='tw-m-2' name={"arrows alternate horizontal"} size='large' />
                    )}
                    <div aria-label={`${label} ${conditionLabel} End`} className='tw-flex tw-w-full'>
                      <DateTime
                        value={endDate}
                        onChange={(date?: Date) => {
                          setEndDate(date);
                          onCalendarDateChange?.({ startDate, endDate: date });
                        }}
                        className='tw-w-full tw-bg-white'
                        granularity={useTimeSelection ? "minute" : "day"}
                        clearable={clearable}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </Group>
          <div className='tw-mt-2 tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-2'>
            <div className='tw-text-[12px] tw-text-grey'>{datePreviewText}</div>
            <Button
              className='tw-m-0 tw-cursor-pointer tw-border-none tw-bg-transparent tw-bg-none tw-p-0 tw-font-[lato] tw-text-[12px] tw-font-semibold tw-text-blue'
              onPress={() => {
                setUseRelativeDates(!useRelativeDates);
                const newStartDate = startRelativeDate || startDateFromDefaultOptions;
                const newEndDate = endRelativeDate || endDateFromDefaultOptions;
                if (useRelativeDates) {
                  //we are switching to calendar dates, so we need to call the calendar date change handler
                  const newCalendarStartDate = showBothDatePickers || prioritizeStartDate ? newStartDate : undefined;
                  const newCalendarEndDate = showBothDatePickers || !prioritizeStartDate ? newEndDate : undefined;

                  setStartDate(newCalendarStartDate);
                  setEndDate(newCalendarEndDate);
                  onCalendarDateChange?.({
                    startDate: newCalendarStartDate,
                    endDate: newCalendarEndDate,
                  });
                  return;
                }
                onRelativeDateChange?.({
                  startDate: newStartDate,
                  endDate: newEndDate,
                  dateOption,
                });
              }}
            >
              {dateTypeButtonText}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface IRelativeDatePickerProps {
  defaultValue?: string;
  options?: DropdownItemProps[];
  onChange?: (value: { value: string; startDate?: Date; endDate?: Date; errorMessage?: string }) => void;
  className?: string;
  value?: string;
  clearable?: boolean;
}

const RelativeDatePicker = ({
  onChange,
  options,
  defaultValue,
  className,
  value,
  clearable,
}: IRelativeDatePickerProps) => {
  const timezoneInfo = useTimezone({ defaultToAgencyTimezone: true });
  const { defaultTimezoneInfo, daysOff, isLoading, startOfFiscalYear } = timezoneInfo;
  const addUserDefinedDateOption = (defaultOptions: any[], userOption: string) => {
    if (!userOption) return defaultOptions;
    const userOptionIndex = defaultOptions.findIndex((option) => option.value === userOption);
    if (userOptionIndex === -1) {
      const userHeaderIndex = defaultOptions.findIndex((option) => option.header && option.key === "user-group");
      if (userHeaderIndex === -1) {
        return [
          {
            key: "user-group",
            text: <strong>User Defined</strong>,
            header: true,
            className: "tw-opacity-100",
            disabled: true,
          },
          {
            key: defaultOptions.length,
            text: userOption,
            value: userOption,
          },
          ...defaultOptions,
        ];
      }
      return defaultOptions
        .slice(0, userHeaderIndex + 1)
        .concat([
          { key: defaultOptions.length, text: userOption, value: userOption },
          ...defaultOptions.slice(userHeaderIndex + 1),
        ]);
    }
    return defaultOptions;
  };
  const [currentOptions, setCurrentOptions] = useState<DropdownItemProps[]>(
    addUserDefinedDateOption(options ?? DefaultAdvancedDateFilterOptions, value ?? ""),
  );
  return (
    <Dropdown
      className={className}
      search
      selection
      allowAdditions
      options={currentOptions}
      defaultValue={defaultValue}
      loading={isLoading}
      disabled={isLoading}
      value={value}
      clearable={clearable}
      onChange={(_, { value }) => {
        if (isLoading) return;
        const { startDate, endDate, errorMessage } =
          getDatesFromDateOption(value as string, startOfFiscalYear, daysOff, defaultTimezoneInfo.timezone) ?? {};
        onChange?.({ value: value as string, startDate, endDate, errorMessage });
      }}
      onAddItem={(_, { value }) => {
        if (isLoading) return;
        const { startDate, endDate, errorMessage } =
          getDatesFromDateOption(value as string, startOfFiscalYear, daysOff, defaultTimezoneInfo.timezone) ?? {};
        onChange?.({ value: value as string, startDate, endDate, errorMessage });
        if (!errorMessage) {
          setCurrentOptions(addUserDefinedDateOption(currentOptions, value as string));
        }
      }}
    />
  );
};

interface IControlledDateRangeFilterProps extends IBaseControlProps, IDateRangeFilterProps {
  setError: (name: string, error: FieldError, config: { shouldFocus?: boolean }) => void;
}

export const ControlledDateRangeFilter = ({
  control,
  name,
  errors,
  setError,
  ...props
}: IControlledDateRangeFilterProps) => {
  const errorMessage = nestedErrorMessageSelector(errors)(name);
  return (
    <Controller
      control={control}
      name={name}
      errors={errors}
      defaultValue={{
        dateCondition: props.defaultOperator || "Equal",
        dateValue: {
          startDate: props.defaultStartDate,
          endDate: props.defaultEndDate,
          dateOption: props.defaultDateOption,
        },
      }}
      render={({ onChange, value: formValue }) => (
        <>
          <DateRangeFilter
            {...props}
            defaultDateOption={formValue?.dateOption || props.defaultDateOption}
            defaultStartDate={formValue?.startDate || props.defaultStartDate}
            defaultEndDate={formValue?.endDate || props.defaultEndDate}
            defaultOperator={formValue?.dropdownCondition || props.defaultOperator}
            onCalendarDateChange={({ startDate, endDate }) => {
              onChange({
                ...formValue,
                dateValue: {
                  startDate,
                  endDate,
                  dateOption: undefined,
                },
              });
              if (props.onCalendarDateChange) {
                props.onCalendarDateChange({ startDate, endDate });
              }
            }}
            onRelativeDateChange={({ startDate, endDate, dateOption, errorMessage }) => {
              console.log(formValue);
              if (errorMessage) {
                setError(name, { type: "manual", message: errorMessage }, { shouldFocus: true });
                return;
              }
              onChange({
                ...formValue,
                dateValue: {
                  startDate: undefined,
                  endDate: undefined,
                  dateOption,
                },
              });
              if (props.onRelativeDateChange) {
                props.onRelativeDateChange({ startDate, endDate, dateOption, errorMessage });
              }
            }}
            onOperatorChange={({ operator, dateOption, endDate, startDate }) => {
              console.log(formValue);
              onChange({
                ...formValue,
                dateCondition: operator,
                dateValue: {
                  startDate,
                  endDate,
                  dateOption,
                },
              });
              if (props.onOperatorChange) {
                props.onOperatorChange({ operator, dateOption, startDate, endDate });
              }
            }}
          />
          {!!errorMessage && (
            <SemanticLabel className='tw-ml-0 tw-w-fit' basic prompt pointing='above'>
              {errorMessage}
            </SemanticLabel>
          )}
        </>
      )}
    />
  );
};
